/* global process */
// src/components/ScraperResults/ScraperResults.js

import axios from "axios";
import { Download } from "lucide-react";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import HomeButton from "../HomeButton/HomeButton";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

const ScraperResult = () => {
	const location = useLocation();
	const results = location.state?.results || [];
	const [exportError, setExportError] = useState(null);

	// Handle csv export for results data
	const handleExportCSV = async () => {
		try {
			setExportError(null);
			const response = await axios.post(
				`${backendUrl}/api/export-properties`,
				{ properties: results },
				{ responseType: "blob" }
			);
			const blob = response.data;

			// Create a download link
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "property-comparison.csv";

			// Trigger download
			document.body.appendChild(a);
			a.click();

			// Cleanup
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error) {
			console.error("Failed to export CSV:", error);
			setExportError(error.message);
		}
	};

	const [filters, setFilters] = useState({
		rating: 0,
		bedrooms: 0,
		bathrooms: 0,
		price: Infinity,
	});

	const applyFilters = () => {
		const filteredResults = location.state?.results.filter((property) => {
			try {
				const { Stars = "", Bedrooms = "", Bathrooms = "", Price = "" } = property;

				// Extract numeric values safely with default fallbacks
				const starValue = parseFloat(Stars.replace(/[^0-9.]+/g, "") || "0");
				const bedroomValue = parseFloat(Bedrooms.replace(/[^0-9.]+/g, "") || "0");
				const bathroomValue = parseFloat(Bathrooms.replace(/[^0-9.]+/g, "") || "0");
				const priceValue = parseFloat(Price.replace(/[^0-9.-]+/g, "") || "0");

				return (
					starValue >= filters.rating &&
					bedroomValue >= filters.bedrooms &&
					bathroomValue >= filters.bathrooms &&
					priceValue <= filters.price
				);
			} catch (error) {
				console.error("Error filtering property:", error, property);
				return false; // Skip properties that cause errors
			}
		});
		setFilteredResults(filteredResults);
	};

	const [filteredResults, setFilteredResults] = useState(results);
	const [chatQuery, setChatQuery] = useState("");
	const [chatHistory, setChatHistory] = useState([]);
	const [promptIndices, setPromptIndices] = useState({});
	const [selectedResponses, setSelectedResponses] = useState({});
	const [useOptimalPrompts, setUseOptimalPrompts] = useState(false);

	const handleSendChat = async () => {
		// Build payload
		const payload = {
			query: chatQuery,
			listings: results.map((property) => ({
				description: property.Description || "",
				metaDescription: property.MetaDescription || "",
			})),
		};

		// If not using optimal prompts, randomly select 2 from all available prompts
		if (!useOptimalPrompts) {
			const allPrompts = [0, 1, 2];
			const shuffled = [...allPrompts].sort(() => Math.random() - 0.5);
			const selectedPrompts = shuffled.slice(0, 2);
			payload.prompts = selectedPrompts;
		}
		// Otherwise, don't include prompts and let the backend choose best ones by ELO score

		try {
			const { data } = await axios.post(`${backendUrl}/api/chat`, payload, {
				headers: { "Content-Type": "application/json" },
			});

			const chatId = Date.now().toString();
			setChatHistory([
				...chatHistory,
				{
					id: chatId,
					query: chatQuery,
					responses: data.responses,
				},
			]);

			// Store the prompt indices for this chat
			setPromptIndices({
				...promptIndices,
				[chatId]: data.prompt_indices,
			});

			setChatQuery("");
		} catch (error) {
			console.error("AI Chat error:", error);
		}
	};

	const handleResponseSelect = async (chatId, responseIndex) => {
		// Mark this response as selected
		setSelectedResponses({
			...selectedResponses,
			[chatId]: responseIndex,
		});

		// Get the prompt indices for this chat
		const indices = promptIndices[chatId];
		if (!indices || indices.length < 2) return;

		// Create a ranked array of prompts based on user preference
		// Start with the selected prompt as most preferred
		const rankedPrompts = [];

		// First add the winning prompt
		rankedPrompts.push(indices[responseIndex]);

		// Then add all other prompts in their original order
		indices.forEach((promptIndex, idx) => {
			if (idx !== responseIndex) {
				rankedPrompts.push(promptIndex);
			}
		});

		try {
			await axios.post(`${backendUrl}/api/record-prompt-preference`, {
				ranked_prompts: rankedPrompts,
			});
			console.log(`Recorded preference ranking: ${rankedPrompts.join(" > ")}`);
		} catch (error) {
			console.error("Error recording preference:", error);
		}
	};

	return (
		<div className="container p-8 mx-auto">
			<div className="space-y-4 mb-8">
				<HomeButton />
				<div className="flex flex-col items-center gap-2">
					<button
						onClick={handleExportCSV}
						className="flex items-center px-6 py-3 font-semibold text-white transition-all duration-300 transform bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full shadow-md hover:scale-105 hover:shadow-xl"
					>
						<Download className="w-5 h-5 mr-2" />
						Export to CSV
					</button>
					{exportError && <p className="text-red-500 text-sm">{exportError}</p>}
				</div>
			</div>
			<div className="mb-8 p-4 bg-white rounded-lg shadow-md">
				<h2 className="text-xl font-bold mb-4">Filter Properties</h2>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						applyFilters();
					}}
				>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
						<div>
							<label htmlFor="rating-input" className="block text-sm font-medium text-gray-700 mb-2">
								Minimum Rating
							</label>
							<input
								id="rating-input"
								type="number"
								min="0"
								max="5"
								step="0.1"
								aria-label="Filter by minimum rating"
								className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
								onChange={(e) => setFilters({ ...filters, rating: parseFloat(e.target.value) || 0 })}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										applyFilters();
									}
								}}
							/>
						</div>
						<div>
							<label htmlFor="bedrooms-input" className="block text-sm font-medium text-gray-700 mb-2">
								Minimum Bedrooms
							</label>
							<input
								id="bedrooms-input"
								type="number"
								min="0"
								aria-label="Filter by minimum bedrooms"
								className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
								onChange={(e) => setFilters({ ...filters, bedrooms: parseInt(e.target.value) || 0 })}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										applyFilters();
									}
								}}
							/>
						</div>
						<div>
							<label htmlFor="bathrooms-input" className="block text-sm font-medium text-gray-700 mb-2">
								Minimum Bathrooms
							</label>
							<input
								id="bathrooms-input"
								type="number"
								min="0"
								aria-label="Filter by minimum bathrooms"
								className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
								onChange={(e) => setFilters({ ...filters, bathrooms: parseInt(e.target.value) || 0 })}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										applyFilters();
									}
								}}
							/>
						</div>
						<div>
							<label htmlFor="price-input" className="block text-sm font-medium text-gray-700 mb-2">
								Maximum Price
							</label>
							<input
								id="price-input"
								type="number"
								min="0"
								aria-label="Filter by maximum price"
								className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
								onChange={(e) =>
									setFilters({ ...filters, price: parseFloat(e.target.value) || Infinity })
								}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										applyFilters();
									}
								}}
							/>
						</div>
					</div>
					<button
						type="submit"
						aria-label="Apply property filters"
						className="flex items-center px-6 py-3 mt-4 font-semibold text-white transition-all duration-300 transform bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full shadow-md hover:scale-105 hover:shadow-xl"
					>
						Apply Filters
					</button>
				</form>
			</div>
			<h1
				className="mb-8 text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 bg-[200%_auto] opacity-0 animate-fade-in"
				style={{ animationDelay: "0.3s" }}
			>
				Property Comparison
			</h1>
			<div className="p-8 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 bg-[200%_200%] animate-gradient-move rounded-2xl shadow-2xl">
				{filteredResults.length > 0 ? (
					<div className="flex space-x-6 overflow-x-auto">
						{filteredResults.map((property, index) => (
							<PropertyColumn key={index} property={property} />
						))}
					</div>
				) : (
					<div className="text-center py-12">
						<h3 className="text-2xl font-semibold text-white mb-4">No Properties Found</h3>
						<p className="text-white opacity-80">
							No properties match your current filter criteria. Try adjusting your filters for more
							results.
						</p>
					</div>
				)}
			</div>
			<h1
				className="mt-8 mb-8 text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 bg-[200%_auto] opacity-0 animate-fade-in"
				style={{ animationDelay: "0.3s" }}
			>
				AI Chat
			</h1>

			<div className="bg-white shadow-md rounded-lg p-6 mt-4">
				{chatHistory.length > 0 && (
					<div className="mb-4">
						{chatHistory.map((chat) => (
							<div key={chat.id} className="mb-6 border-b pb-4">
								<p className="font-semibold">Your query: {chat.query}</p>
								<div className="flex space-x-4 mt-2">
									{chat.responses.map((res, index) => (
										<div
											key={index}
											className={`flex-1 p-3 rounded-md ${
												Object.prototype.hasOwnProperty.call(selectedResponses, chat.id) &&
												selectedResponses[chat.id] === index
													? "bg-green-50 border-2 border-green-500"
													: "bg-gray-100"
											}`}
										>
											<div className="mb-2">{res}</div>
											{!Object.prototype.hasOwnProperty.call(selectedResponses, chat.id) && (
												<button
													onClick={() => handleResponseSelect(chat.id, index)}
													className="mt-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition"
												>
													I like this response
												</button>
											)}
										</div>
									))}
								</div>
								{Object.prototype.hasOwnProperty.call(selectedResponses, chat.id) && (
									<p className="text-green-600 mt-2 text-sm">Thank you for your feedback!</p>
								)}
							</div>
						))}
					</div>
				)}
				<h2 className="text-2xl font-bold mb-4">Ask about this listing</h2>
				<textarea
					value={chatQuery}
					onChange={(e) => setChatQuery(e.target.value)}
					placeholder="Enter your query here"
					className="w-full p-4 border rounded-lg mb-2"
					rows="3"
				/>
				<div className="flex items-center mb-4">
					<input
						type="checkbox"
						id="use-optimal-prompts"
						checked={useOptimalPrompts}
						onChange={() => setUseOptimalPrompts(!useOptimalPrompts)}
						className="mr-2"
					/>
					<label htmlFor="use-optimal-prompts" className="text-sm text-gray-600">
						Use optimal prompts (based on feedback ranking)
					</label>
				</div>
				<button
					onClick={handleSendChat}
					className="w-full px-6 py-3 font-semibold text-white bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full shadow-md hover:scale-105"
				>
					Send
				</button>
			</div>
		</div>
	);
};

// Property column component with enhanced effects
const PropertyColumn = ({ property }) => {
	const { Title, Stars, Type, Bedrooms, Bathrooms, Description, MetaDescription, ImageURL, Price, BookingURL } =
		property;

	const handleBookNowClick = (e) => {
		e.preventDefault();
		if (BookingURL) {
			window.open(BookingURL, "_blank", "noopener,noreferrer");
		}
	};

	return (
		<div className="flex-shrink-0 overflow-hidden transition-transform duration-300 ease-in-out transform shadow-2xl w-80 bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-600 rounded-2xl hover:shadow-3xl hover:bg-opacity-90">
			<img src={ImageURL} alt={Title} className="object-cover w-full h-64 rounded-t-2xl" />

			<div className="p-8 space-y-4 text-white">
				<AnimatedText className="text-3xl font-extrabold tracking-tight">{Title}</AnimatedText>

				<AnimatedText className="text-lg text-yellow-400">{Stars}</AnimatedText>

				<AnimatedText className="italic text-indigo-200 text-md">{Type}</AnimatedText>

				<AnimatedText className="text-lg">
					<strong>Bedrooms:</strong> {Bedrooms}
				</AnimatedText>

				<AnimatedText className="text-lg">
					<strong>Bathrooms:</strong> {Bathrooms}
				</AnimatedText>

				<AnimatedText className="mt-4 text-sm leading-relaxed text-indigo-100">{Description}</AnimatedText>

				<AnimatedText className="mt-2 text-xs italic text-indigo-300">{MetaDescription}</AnimatedText>

				<div className="flex items-center justify-between mt-6">
					<AnimatedText className="text-2xl font-semibold">{Price}</AnimatedText>

					<a
						href={BookingURL}
						target="_blank"
						rel="noopener noreferrer"
						className="px-6 py-2 font-semibold text-indigo-600 transition-all duration-300 transform bg-white rounded-full shadow-md hover:bg-indigo-600 hover:text-white hover:scale-125 hover:shadow-xl"
						onClick={handleBookNowClick}
					>
						Book Now
					</a>
				</div>
			</div>
		</div>
	);
};

// Animated text component with smooth slide-in effects
const AnimatedText = ({ children, className }) => {
	return (
		<div
			className={`${className} opacity-0 translate-y-6 animate-slide-in`}
			style={{ animationDelay: `${Math.random() * 0.5 + 0.5}s` }}
		>
			{children}
		</div>
	);
};

export default ScraperResult;
